import { useCallback, useMemo, useRef } from 'react';

/**
 * Счётчики о том, КАК писалась работа: сколько набрано руками, сколько пришло
 * вставкой, сколько заняло времени, сколько раз возвращались править.
 *
 * ЗАЧЕМ. Куратор должен видеть факты, а не догадки. «Отправлено через 1 мин
 * 50 с, текст вставлен целиком, правок не было» — это разговор по делу.
 * Разбирать сам текст на «машинность» мы принципиально не стали: покупные
 * детекторы ошибаются именно на неносителях языка, а наши ученики пишут
 * заученными конструкциями, потому что их этому учат.
 *
 * ЗДЕСЬ НЕТ СОДЕРЖИМОГО РАБОТЫ. Наружу уходят только числа — ни куска текста,
 * ни того, что именно вставили. Счётчики видит куратор, ученик — никогда.
 *
 * ЭТО НЕ ЗАЩИТА. Числа считает браузер, и подделать их можно. Границей
 * безопасности они не служат: ни одна проверка на них не завязана, они лишь
 * повод посмотреть работу внимательнее.
 */
export interface DraftTelemetry {
  typed_chars: number;
  pasted_chars: number;
  paste_count: number;
  largest_paste: number;
  duration_sec: number;
  active_sec: number;
  deletions: number;
  mid_edits: number;
}

/** Разовая вставка такого размера набором быть не может — считаем вставкой,
 *  даже если событие paste до нас не дошло (перетаскивание текста мышью). */
const BURST_CHARS = 30;

/** Удаление от такого размера считается стиранием куска, а не опечаткой. */
const BULK_DELETE = 20;

/** Пауза длиннее — это перерыв, а не работа: ученик мог открыть задание утром,
 *  а сесть за него вечером. В активное время такие промежутки не входят. */
const BREAK_MS = 120_000;

interface State {
  typed: number;
  pasted: number;
  pastes: number;
  largest: number;
  deletions: number;
  midEdits: number;
  firstAt: number;
  lastAt: number;
  activeMs: number;
  prevLen: number;
  pendingPaste: number;
}

const fresh = (): State => ({
  typed: 0,
  pasted: 0,
  pastes: 0,
  largest: 0,
  deletions: 0,
  midEdits: 0,
  firstAt: 0,
  lastAt: 0,
  activeMs: 0,
  prevLen: 0,
  pendingPaste: 0,
});

export function useDraftTelemetry() {
  const s = useRef<State>(fresh());

  const reset = useCallback((initialLength = 0) => {
    s.current = { ...fresh(), prevLen: initialLength };
  }, []);

  /** Повесить на onPaste поля ввода. */
  const onPaste = useCallback((e: React.ClipboardEvent) => {
    const text = e.clipboardData?.getData('text') ?? '';
    // Запоминаем намерение: сколько символов реально дойдёт до текста, станет
    // известно в onChange — вставка может заменить выделенный кусок.
    s.current.pendingPaste = text.length;
  }, []);

  /**
   * Повесить на onChange поля ввода — ПОСЛЕ своего setState, значение и
   * позицию курсора берём из события.
   */
  const onChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const st = s.current;
    const now = Date.now();
    const value = e.target.value;
    const len = value.length;
    const delta = len - st.prevLen;

    if (!st.firstAt) st.firstAt = now;
    if (st.lastAt) {
      const gap = now - st.lastAt;
      if (gap < BREAK_MS) st.activeMs += gap;
    }
    st.lastAt = now;

    if (delta > 0) {
      // Вставка: либо было событие paste, либо прирост слишком велик для
      // набора руками.
      const pasted = st.pendingPaste > 0 ? Math.min(delta, st.pendingPaste) : delta >= BURST_CHARS ? delta : 0;
      if (pasted > 0) {
        st.pasted += pasted;
        st.pastes += 1;
        if (pasted > st.largest) st.largest = pasted;
      }
      st.typed += delta - pasted;
    } else if (delta <= -BULK_DELETE) {
      st.deletions += 1;
    }
    st.pendingPaste = 0;

    // Правка в середине: курсор стоит не в конце — значит вернулись к уже
    // написанному. У текста, перенесённого готовым, таких возвратов нет.
    const caret = (e.target as HTMLTextAreaElement).selectionStart;
    if (typeof caret === 'number' && caret < len) st.midEdits += 1;

    st.prevLen = len;
  }, []);

  /** Итог для отправки на сервер. */
  const snapshot = useCallback((): DraftTelemetry => {
    const st = s.current;
    const durationMs = st.firstAt && st.lastAt ? st.lastAt - st.firstAt : 0;
    return {
      typed_chars: st.typed,
      pasted_chars: st.pasted,
      paste_count: st.pastes,
      largest_paste: st.largest,
      duration_sec: Math.round(durationMs / 1000),
      active_sec: Math.round(st.activeMs / 1000),
      deletions: st.deletions,
      mid_edits: st.midEdits,
    };
  }, []);

  // ⚠️ ОДИН И ТОТ ЖЕ ОБЪЕКТ МЕЖДУ ОТРИСОВКАМИ (12.09.2026). Окно «Отправить заново»
  // держит его в зависимостях эффекта сброса формы: новый объект на каждой
  // отрисовке запускал эффект по кругу — открытое окно крутилось вхолостую и
  // стирало набранный текст. Сторож — `draftTelemetryStable.test.ts`.
  return useMemo(() => ({ onPaste, onChange, snapshot, reset }), [onPaste, onChange, snapshot, reset]);
}
